import React, { useState, useRef, useEffect } from 'react';
import { Input, Button, Typography, Space, message } from 'antd';
import type { InputRef } from 'antd';
import { CheckCircleOutlined, ReloadOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import './OTPVerification.css';

const { Title, Text } = Typography;

interface OTPVerificationProps {
  phone?: string;
  email?: string;
  isDualVerification?: boolean;
  onVerify: (otp: string, secondaryOtp?: string) => Promise<void>;
  onResend?: () => Promise<void>;
  loading?: boolean;
  onCancel?: () => void;
}

const OTPVerification: React.FC<OTPVerificationProps> = ({
  phone,
  email,
  isDualVerification = false,
  onVerify,
  onResend,
  loading = false,
  onCancel,
}) => {
  const [otp, setOtp] = useState<string[]>(['', '', '', '', '', '']);
  const [secondaryOtp, setSecondaryOtp] = useState<string[]>(['', '', '', '', '', '']);
  const [resendLoading, setResendLoading] = useState(false);
  const [timer, setTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRefs = useRef<(InputRef | null)[]>([]);
  const secondaryInputRefs = useRef<(InputRef | null)[]>([]);

  useEffect(() => {
    // Focus first input on mount
    inputRefs.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (timer > 0 && !canResend) {
      const interval = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [timer, canResend]);

  const handleChange = (index: number, value: string, isSecondary: boolean = false) => {
    // Only allow digits and limit to 1
    if (value && (!/^\d$/.test(value) || value.length > 1)) {
      return;
    }

    if (isSecondary) {
      const newOtp = [...secondaryOtp];
      newOtp[index] = value;
      setSecondaryOtp(newOtp);

      if (value && index < 5) {
        secondaryInputRefs.current[index + 1]?.focus();
      }
    } else {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      if (value && index < 5) {
        inputRefs.current[index + 1]?.focus();
      } else if (value && index === 5 && isDualVerification) {
        // Move to secondary inputs if dual
        secondaryInputRefs.current[0]?.focus();
      }
    }
    // Clear error on user input
    setError(null);
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>, isSecondary: boolean = false) => {
    if (e.key === 'Backspace') {
      if (isSecondary) {
        if (!secondaryOtp[index] && index > 0) {
          secondaryInputRefs.current[index - 1]?.focus();
        } else if (!secondaryOtp[index] && index === 0) {
          // Move back to primary
          inputRefs.current[5]?.focus();
        }
      } else {
        if (!otp[index] && index > 0) {
          inputRefs.current[index - 1]?.focus();
        }
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent, isSecondary: boolean = false) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();

    if (/^\d{6}$/.test(pastedData)) {
      const digits = pastedData.split('');
      if (isSecondary) {
        setSecondaryOtp(digits);
        secondaryInputRefs.current[5]?.focus();
      } else {
        setOtp(digits);
        if (isDualVerification) {
          secondaryInputRefs.current[0]?.focus();
        } else {
          inputRefs.current[5]?.focus();
          // Auto-verify only if single
          setTimeout(() => {
            handleVerify(pastedData);
          }, 100);
        }
      }
      setError(null);
    } else {
      message.error('Please paste a valid 6-digit OTP');
    }
  };

  const handleVerify = async (otpValue?: string) => {
    setError(null);
    const primaryOtpToVerify = otpValue || otp.join('');
    const secondaryOtpToVerify = secondaryOtp.join('');

    if (primaryOtpToVerify.length !== 6) {
      setError(`Please enter all 6 digits for ${isDualVerification ? 'phone' : 'verification'}`);
      return;
    }

    if (isDualVerification && secondaryOtpToVerify.length !== 6) {
      setError('Please enter all 6 digits for email');
      return;
    }

    if (!/^\d{6}$/.test(primaryOtpToVerify) || (isDualVerification && !/^\d{6}$/.test(secondaryOtpToVerify))) {
      setError('OTP must contain only digits');
      return;
    }

    try {
      if (isDualVerification) {
        await onVerify(primaryOtpToVerify, secondaryOtpToVerify);
      } else {
        await onVerify(primaryOtpToVerify);
      }
    } catch (error: any) {
      setError(error.message || 'OTP verification failed');
      // Clear OTP on error
      setOtp(['', '', '', '', '', '']);
      if (isDualVerification) setSecondaryOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    }
  };

  const handleResend = async () => {
    if (!canResend) return;

    setResendLoading(true);
    setError(null);
    try {
      if (onResend) {
        await onResend();
        message.success('OTP resent successfully!');
      }
      setOtp(['', '', '', '', '', '']);
      if (isDualVerification) setSecondaryOtp(['', '', '', '', '', '']);
      setTimer(60);
      setCanResend(false);
      inputRefs.current[0]?.focus();
    } catch (error: any) {
      setError(error.message || 'Failed to resend OTP');
    } finally {
      setResendLoading(false);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="otp-verification-container">
      <div className="otp-verification-header" style={{ position: 'relative' }}>
        {onCancel && (
          <Button
            type="text"
            onClick={onCancel}
            disabled={loading}
            className="otp-back-button"
            icon={<ArrowLeftOutlined />}
            style={{ position: 'absolute', left: 0, top: 0 }}
          />
        )}
        <div className="otp-icon-wrapper">
          <CheckCircleOutlined className="otp-icon" />
        </div>
        <Title level={3} className="otp-title">
          Verify Your Account
        </Title>
        <Text type="secondary" className="otp-subtitle">
          We've sent a 6-digit verification code
        </Text>

        <Text type="secondary" style={{ display: 'block', fontSize: '12px', marginTop: '4px' }}>
          Check your messages
        </Text>
      </div>

      {error && (
        <div style={{
          color: '#ff4d4f',
          backgroundColor: '#fff2f0',
          border: '1px solid #ffccc7',
          padding: '8px 12px',
          borderRadius: '4px',
          marginBottom: '16px',
          textAlign: 'center',
          fontSize: '14px'
        }}>
          {error}
        </div>
      )}

      <div className="otp-input-container">
        <Space direction="vertical" align="center" style={{ width: '100%' }}>
          {isDualVerification && <Text strong>OTP received on SMS</Text>}
          <Text type="secondary" style={{ fontSize: '12px' }}>Mobile: {phone}</Text>
          <Space size="middle" className="otp-inputs">
            {otp.map((digit, index) => (
              <Input
                key={index}
                ref={(el) => {
                  inputRefs.current[index] = el;
                }}
                className={`otp-input ${digit ? 'otp-input-filled' : ''}`}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value, false)}
                onKeyDown={(e) => handleKeyDown(index, e, false)}
                onPaste={(e) => index === 0 ? handlePaste(e, false) : undefined}
                maxLength={1}
                disabled={loading}
                autoComplete="off"
                inputMode="numeric"
              />
            ))}
          </Space>

          {isDualVerification && (
            <>
              <Text strong style={{ marginTop: 16, display: 'block' }}>OTP received on Email</Text>
              <Text type="secondary" style={{ fontSize: '12px' }}>Email: {email}</Text>
              <Space size="middle" className="otp-inputs">
                {secondaryOtp.map((digit, index) => (
                  <Input
                    key={index}
                    ref={(el) => {
                      secondaryInputRefs.current[index] = el;
                    }}
                    className={`otp-input ${digit ? 'otp-input-filled' : ''}`}
                    value={digit}
                    onChange={(e) => handleChange(index, e.target.value, true)}
                    onKeyDown={(e) => handleKeyDown(index, e, true)}
                    onPaste={(e) => index === 0 ? handlePaste(e, true) : undefined}
                    maxLength={1}
                    disabled={loading}
                    autoComplete="off"
                    inputMode="numeric"
                  />
                ))}
              </Space>
            </>
          )}
        </Space>
      </div>

      <div className="otp-actions">
        <Button
          type="primary"
          size="large"
          block
          onClick={() => handleVerify()}
          loading={loading}
          disabled={otp.join('').length !== 6 || (isDualVerification && secondaryOtp.join('').length !== 6)}
          className="otp-verify-button"
        >
          Verify OTP
        </Button>

        <div className="otp-resend-section">
          {canResend ? (
            <Button
              type="link"
              onClick={handleResend}
              loading={resendLoading}
              icon={<ReloadOutlined />}
              className="otp-resend-button"
            >
              Resend OTP
            </Button>
          ) : (
            <Text type="secondary" className="otp-timer">
              Resend OTP in {formatTime(timer)}
            </Text>
          )}
        </div>


      </div>
    </div>
  );
};

export default OTPVerification;
